'use client'

import Script from 'next/script'
import {
  CAPABILITY_PATH_PREFIXES,
  CREDENTIAL_QUERY_PARAMS,
  CREDENTIAL_QUERY_PATH_SEGMENTS,
} from '@/lib/analytics/first-party-tracking-scope'
import { useCookieConsent, useFirstPartyTrackingSurface } from '@/lib/privacy/use-cookie-consent'

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_ID || 'G-2HHR1MCXF4'
const APP_ROOT_DOMAIN = (process.env.NEXT_PUBLIC_APP_ROOT_DOMAIN || 'maiteams.com').toLowerCase()
// Lead-form hosts (`<sub>.leads.maiteams.com`) are rewritten server-side to
// /forms/<sub>, so the BROWSER pathname is "/" and only the hostname can
// identify them — the prefix list alone is blind to the branded case.
const LEAD_FORM_DOMAIN = (process.env.NEXT_PUBLIC_LEAD_FORM_DOMAIN || 'leads.yourdomain.com').toLowerCase()

/**
 * Rendered only once the visitor's cookie choice allows analytics AND no
 * Global Privacy Control signal is on (`lib/preferences/cookie-consent.ts`):
 * Google may use Analytics data for its own purposes, so GPC treats it as a
 * possible sale or share and it stays off. Until then nothing from Google is
 * requested and no `_ga` cookie is set. The inline guards below still apply
 * once it renders.
 *
 * Google signals and ad personalization are off in the tag itself (owner
 * decision D74(d), 2026-09-29), whatever the property's settings say: our
 * Analytics data is never joined to Google accounts for cross-device
 * reporting or used to personalize ads.
 */
export default function GoogleAnalytics() {
  const consent = useCookieConsent()
  const surface = useFirstPartyTrackingSurface()
  if (!surface || !consent.permissions.saleOrSharing) return null
  return (
    <Script id="google-analytics" strategy="afterInteractive">
      {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          // Customer white-label landing-page hosts and capability URLs must NOT
          // send page views into MaiTeams' GA property (Codex). A capability URL
          // is a credential, not a tracking-consent question. The gtag.js LOADER
          // is appended from inside the same qualifying branch (mirroring
          // new-relic-browser.tsx): customer pixels on published pages load
          // their own gtag.js with their own measurement id
          // (lib/landing-pages/pixels.ts), so MaiTeams' loader has no business
          // on customer traffic. /p/ and /lp/domain/ are excluded because a
          // customer's page served from the app host is still customer traffic;
          // so are /forms/ + /embed/ (customer lead capture — also reachable
          // via the lead-form HOSTNAME, which the server rewrite hides from
          // pathname checks) and the token surfaces (/public/preferences,
          // /public/unsubscribe, /share/). Prefixes match with and without the
          // trailing slash (the bare "/p" 404 carries the LP CSP too). The list
          // is shared with the Dub loader (lib/analytics/first-party-tracking-scope.ts)
          // so the two can never disagree about where MaiTeams may track.
          (function(){
            var h = (location.hostname || '').toLowerCase();
            var capabilityPrefixes = ${JSON.stringify([...CAPABILITY_PATH_PREFIXES])};
            var credentialPaths = ${JSON.stringify(CREDENTIAL_QUERY_PATH_SEGMENTS.map((segment) => '/' + segment))};
            var credentialParams = ${JSON.stringify([...CREDENTIAL_QUERY_PARAMS])};
            var isApp = h === '${APP_ROOT_DOMAIN}' || h.endsWith('.${APP_ROOT_DOMAIN}') || h === 'localhost' || h.endsWith('.localhost') || h === '127.0.0.1' || h.endsWith('.vercel.app');
            var isLeadFormHost = h === '${LEAD_FORM_DOMAIN}' || h.endsWith('.${LEAD_FORM_DOMAIN}');
            // The same decision as isFirstPartyTrackingSurface, for ANY url (not
            // just the one at injection time).
            function pathAllowed(pathname, search) {
              var p = (pathname || '/').replace(/%([0-9a-f]{2})/gi, function(m,x){return String.fromCharCode(parseInt(x,16));}).toLowerCase();
              if (capabilityPrefixes.some(function(prefix){ return p === prefix.slice(0, -1) || p.indexOf(prefix) === 0; })) return false;
              var np = p.replace(/[/]+$/, '') || '/';
              if (search && credentialPaths.some(function(c){ return np === c || np.indexOf(c + '/') === 0; })) {
                var params = new URLSearchParams(search);
                if (credentialParams.some(function(n){ return params.getAll(n).some(function(v){ return v.length > 0; }); })) return false;
              }
              return true;
            }
            function allowed(pathname, search) {
              if (!isApp || isLeadFormHost) return false;
              return pathAllowed(pathname, search);
            }
            // A page view reports the PREVIOUS url as its referrer (dr), and a
            // capability URL is a credential, so the first page view after
            // leaving an excluded URL is dropped (accepted loss): the opt-out
            // flag stays on through it and is released a few seconds after gtag.js
            // has loaded, because GA4's history page view can fire well after
            // pushState. The same applies to a full page load whose
            // document.referrer is an excluded URL of ours or a lead-form host;
            // there the referrer is also reduced to its origin.
            var holdMs = 5000;
            var ref = null;
            try { if (document.referrer) ref = new URL(document.referrer); } catch (e) { ref = null; }
            var refExcluded = false;
            var refOrigin = '';
            if (ref) {
              var rh = (ref.hostname || '').toLowerCase();
              var refApp = rh === '${APP_ROOT_DOMAIN}' || rh.endsWith('.${APP_ROOT_DOMAIN}') || rh === 'localhost' || rh.endsWith('.localhost') || rh === '127.0.0.1' || rh.endsWith('.vercel.app');
              var refLead = rh === '${LEAD_FORM_DOMAIN}' || rh.endsWith('.${LEAD_FORM_DOMAIN}');
              refExcluded = refLead || (refApp && !pathAllowed(ref.pathname, ref.search));
              refOrigin = ref.origin;
            }
            var loaded = false;
            var ready = false;
            var hold = refExcluded;
            var prevAllowed = !refExcluded;
            var releaseTimer = null;
            var disableKey = 'ga-disable-${GA_MEASUREMENT_ID}';
            function release() {
              if (releaseTimer) clearTimeout(releaseTimer);
              releaseTimer = setTimeout(function(){
                releaseTimer = null;
                hold = false;
                if (prevAllowed) window[disableKey] = false;
              }, holdMs);
            }
            function load() {
              if (loaded || !isApp || isLeadFormHost) return;
              loaded = true;
              var s = document.createElement('script');
              s.src = 'https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}';
              s.async = true;
              s.onload = function(){ ready = true; if (hold) release(); };
              document.head.appendChild(s);
              if (refExcluded) gtag('config', '${GA_MEASUREMENT_ID}', { page_referrer: refOrigin, allow_google_signals: false, allow_ad_personalization_signals: false });
              else gtag('config', '${GA_MEASUREMENT_ID}', { allow_google_signals: false, allow_ad_personalization_signals: false });
            }
            // Google's documented opt-out flag: while it is true gtag.js drops every
            // hit, including the one a delayed loader sends for the config queued
            // here and GA4's automatic history-change page view. It is
            // re-evaluated BEFORE each client-side navigation takes effect
            // (history wrappers below, installed before gtag.js so GA's own
            // listeners run after) and on popstate.
            function sync(url) {
              var target = location;
              if (url !== undefined && url !== null) {
                try { target = new URL(String(url), location.href); } catch (e) { target = location; }
              }
              var ok = allowed(target.pathname, target.search);
              if (!ok) {
                if (releaseTimer) { clearTimeout(releaseTimer); releaseTimer = null; }
                hold = false;
                prevAllowed = false;
                window[disableKey] = true;
                return;
              }
              if (!prevAllowed) hold = true;
              prevAllowed = true;
              window[disableKey] = hold;
              load();
              if (hold && ready) release();
            }
            sync();
            ['pushState', 'replaceState'].forEach(function(name){
              var original = history[name];
              history[name] = function(state, title, url) {
                sync(url);
                return original.apply(this, arguments);
              };
            });
            window.addEventListener('popstate', function(){ sync(); });
          })();
        `}
    </Script>
  )
}
